/**
 * **Subsystem 2 — the credential must never touch storage.**
 *
 * From [`device-cache.md`](../../../docs/product/device-cache.md)'s **memory-only** table,
 * verbatim in effect:
 *
 * > Fetch just in time, keep **in memory only**, and discard when the operation/tab ends,
 * > authorization fails, the gate changes, or the app backgrounds beyond the operation. **Never**
 * > write them to IndexedDB, localStorage, Cache Storage, logs, or analytics.
 *
 * **This module is where §13's *gate BLE password* criterion is most likely to be broken**, and
 * the reason is proximity rather than difficulty: no earlier PRD ever put a BLE password on a
 * resident's device, and `outbox.ts` — durable client storage — is one directory entry away, in
 * the same feature, written in the same week. Two specific traps, both named in P16 §5:
 *
 *  1. **The outbox is right there.** A receipt carries gate id, action, result, physical
 *     timestamp, transport, the reconciliation id, and the minimum device/session reference.
 *     **Nothing else.** `outbox.ts` states that on its own row type as well.
 *  2. **A "keep it for the session" cache is still not a *persisted* one, and D189(d) turns on
 *     that difference.** Until D189 this module also cleared every credential on
 *     `visibilitychange`, on the reading that a backgrounded PWA is indistinguishable from a
 *     stored one. It is not: nothing was written, and a process that is killed loses the map with
 *     it. What that rule actually cost was the capability — a resident who opened the app at home
 *     and walked to a gate with no reception arrived with nothing held, which is the whole of
 *     what D94 and D61 promise. The clearing is gone; **the prohibition above is unchanged**, and
 *     the bound that replaces it is D61's 24 hours, evaluated by the caller through
 *     `freshness.ts` before a local open is ever offered.
 *
 * **D189(d) supersedes two of `device-cache.md`'s four triggers and makes a third real.** The four
 * are *the operation ends, authorization fails, the gate changes, the app backgrounds*.
 *
 *  - *The app backgrounds* — **gone**, for the reason above.
 *  - *The gate changes* — **gone too, and this is the one that would look like a safe thing to
 *    keep.** D189(e) makes Home an actuator, which means warming a bounded set of gates and
 *    holding those credentials **concurrently**; a rule that empties the map whenever attention
 *    moves to another gate is a rule that deletes the warm copy for the gate the resident is
 *    walking towards. {@link discardCredential} survives as the narrow tool, with no caller.
 *  - *Authorization fails* — **now real, and it was not before.** A refused `ble-access` drops
 *    that gate's copy in {@link withCredential} below. This is the trigger that actually bounds
 *    the exposure a revoked grant creates, and it had no implementation at all.
 *  - *The operation ends* — already wired, through `purge.ts`'s account and erasure paths.
 *
 * On top of those, D61 at the caller: nothing offers a local open at all once the device has gone
 * 24 hours without a successful server sync, whatever this map still holds.
 *
 * **The store is module-level rather than React state on purpose.** A credential in a `useState`
 * is a credential in a component tree that React may keep alive across a route change, in a
 * fiber that a devtools extension can walk, and — most concretely — one that survives the gate
 * changing under it. A single map with one explicit lifetime is smaller and can be emptied.
 */

import type { ApiRequestFn } from '../screens/shared/api'
import { fetchBleAccess, type BleCredential } from './api'

/**
 * One gate's credential, held for as long as the operation lasts and no longer.
 *
 * It is not exported as a type a screen can hold: a component that stored one of these would
 * have re-created the problem this module exists to contain. Screens ask {@link withCredential}
 * to run something *with* a credential, and never to hand them one.
 */
interface HeldCredential {
  readonly credential: BleCredential
  /** The server's short-lived reuse hint, epoch ms. See `BLE_ACCESS_TTL_SECONDS` on the server. */
  readonly expiresAt: number
}

const held = new Map<string, HeldCredential>()

/**
 * Everything goes. Called on sign-out, on a rejected request, and on privacy erasure — the whole
 * of `purge.ts`'s account and erasure paths.
 *
 * **Deliberately not selective.** Every "discard all but the current gate" refinement anyone
 * would want here is a rule that has to stay correct as screens are added, and the cost of being
 * indiscriminate is one extra fetch.
 */
export function discardAllCredentials(): void {
  held.clear()
}

/** *"…or the gate changes"* — the one narrower discard the specification actually names. */
export function discardCredential(gateId: string): void {
  held.delete(gateId)
}

/**
 * Run `operation` with a live credential for `gateId`, fetching one if none is usable.
 *
 * **The credential never leaves this function's scope.** It is handed to the callback for the
 * duration of one operation and is not returned, stored by the caller, or logged — which is why
 * the signature is a continuation rather than a getter. A `getCredential(gateId)` would be one
 * line shorter and would put the value in a variable somebody else owns.
 *
 * @param force re-fetch even if a held copy looks usable — PEC-25's response to an authentication
 *   failure at the controller. **It re-fetches; it never retries with a different password.**
 *   D84 makes guessing a prohibition rather than a preference, because a client that cycled
 *   candidates would be indistinguishable from an attacker probing the controller.
 */
export async function withCredential<T>(
  request: ApiRequestFn,
  gateId: string,
  operation: (credential: BleCredential) => Promise<T>,
  options: {
    readonly force?: boolean
    readonly now?: () => number
    /**
     * **Honour a held credential past the server's reuse hint, and never fetch.** D189(d).
     *
     * `BLE_ACCESS_TTL_SECONDS` is the budget for reusing a copy **while online** — `ble-access.ts`
     * says exactly that on its own constant, and adds that it *"is not D61's window and the two
     * must not be conflated."* This module conflated them: it compared `expiresAt` unconditionally,
     * so five minutes after a successful warm an offline open tried to re-fetch, met a
     * `NetworkError`, and told the resident there was no local access — refusing the capability
     * §13's D61 criterion asserts, for a reason that had nothing to do with their grant.
     *
     * Offline there is no server to re-ask, and the bound that applies is D61's, which the caller
     * has already evaluated through `pickTransport`.
     *
     * **It suppresses the expiry check, not the fetch.** With nothing held this still tries, and
     * that is deliberate: `online: false` means *the last request failed*, not *the radio is off*,
     * and reception returning between that failure and the tap is exactly the case worth
     * recovering. The attempt costs a fast rejection and lands on the same sentence either way.
     */
    readonly offline?: boolean
  } = {},
): Promise<T> {
  const now = options.now ?? (() => Date.now())

  if (options.offline === true && options.force !== true) {
    const offlineCopy = held.get(gateId)
    if (offlineCopy !== undefined) return operation(offlineCopy.credential)
  }

  const existing = held.get(gateId)

  if (options.force === true || existing === undefined || existing.expiresAt <= now()) {
    if (options.force === true) held.delete(gateId)
    const fetched = await fetchBleAccess(request, gateId)
    if (!fetched.ok) {
      /*
       * *"…or authorization fails"* — `device-cache.md`'s second discard trigger, which had no
       * implementation until D189(d) made it load-bearing. A refused `ble-access` is the server
       * saying this grant no longer buys a local open, and a stale copy left in the map would
       * outlive that answer by however long its own hint had to run.
       */
      if (fetched.status === 401 || fetched.status === 403) held.delete(gateId)
      throw new BleAccessRefused(fetched.status, fetched.code)
    }
    held.set(gateId, {
      credential: fetched.data,
      expiresAt: Date.parse(fetched.data.expiresAt),
    })
  }

  const usable = held.get(gateId)
  if (usable === undefined) throw new BleAccessRefused(0, 'no-credential')
  return operation(usable.credential)
}

/**
 * Fetch and hold a credential **ahead of the tap** — the fix for a capability that could not work.
 *
 * **The circle this breaks.** The credential is memory-only (D85) and fetched just-in-time, and
 * until this existed the only production caller of {@link withCredential} was Gate Detail's BLE
 * open. §6's auto-pick chooses BLE while *online* only when `credentialHeld` is already true, so:
 * online → no credential → cloud is picked → the open that would have fetched one never runs;
 * offline → BLE is picked → the fetch needs the network that is off. **Neither branch could ever
 * succeed**, and the failure surfaced as *"couldn't reach the gate over Bluetooth"* on a gate the
 * radio had never addressed. D94's whole promise — *"a cellular outage at the gate means no
 * resident can open it at all"* — was unmet on every platform, and P20's native radio did not
 * change that, because nothing above the leaf ever reached it.
 *
 * **It never throws, and that is deliberate rather than lax.** A pre-fetch is not an event about
 * the gate: nothing was tapped, nothing was sent, and a resident who is merely *looking* at a gate
 * must not be shown an error for a request they did not make. The real open runs
 * {@link withCredential} on its own and reports properly, so every failure here is either
 * recovered there or reported there.
 *
 * **What it costs, stated rather than buried, and D189(d) made the cost larger.** One `ble-access`
 * round trip per gate view, and the password is in memory from the moment the screen opens rather
 * than from the moment the button is pressed. Until D189 the exposure was bounded at one
 * *foreground* view of one gate, because every credential was cleared the instant the app was
 * hidden; it is now bounded at the life of the tab, plus D61's 24 hours at the caller. **D85 is
 * untouched — nothing is written anywhere** — and what the change buys is the capability itself:
 * a credential warmed before the walk to the gate is still held on arrival, which is precisely the
 * case a resident with no reception at the gate is in.
 *
 * Returns whether a usable credential is now held.
 */
export async function warmCredential(
  request: ApiRequestFn,
  gateId: string,
  options: { readonly now?: () => number } = {},
): Promise<boolean> {
  const now = options.now ?? (() => Date.now())
  if (hasCredential(gateId, now())) return true
  try {
    // The operation is a no-op: the fetch-and-hold *is* the work, and routing it through
    // `withCredential` keeps `held` written in exactly one place.
    await withCredential(request, gateId, () => Promise.resolve(undefined), options)
    return hasCredential(gateId, now())
  } catch {
    // Offline, refused, or rate-limited. All three are answered at the tap, not here.
    return false
  }
}

/**
 * Whether a usable credential is already held for a gate — **the only thing outside this module
 * may learn about one.**
 *
 * It exists so the transport picker can tell "BLE would need a round trip" from "BLE is ready
 * now" without seeing a password. It is a boolean and it stays a boolean.
 */
export function hasCredential(gateId: string, now: number = Date.now()): boolean {
  const existing = held.get(gateId)
  return existing !== undefined && existing.expiresAt > now
}

/**
 * Whether a credential is held **at all**, whatever the server's reuse hint says.
 *
 * The offline question, and only that. {@link hasCredential} answers the online one — *would a BLE
 * open need a round trip?* — which is what `pickTransport`'s `fallbackReady` asks and what decides
 * whether the local path is ready before a tap.
 *
 * **Two names rather than one function with a boolean.** They answer genuinely different
 * questions, and a `hasCredential(gateId, now, true)` at a call site reads as configuration rather
 * than as the two distinct facts these are.
 */
export function holdsCredential(gateId: string): boolean {
  return held.has(gateId)
}

/** The server refused to hand over a credential. Carries the code, never the reason it refused. */
export class BleAccessRefused extends Error {
  readonly status: number
  readonly code: string | undefined

  constructor(status: number, code: string | undefined) {
    super('ble-access-refused')
    this.name = 'BleAccessRefused'
    this.status = status
    this.code = code
  }
}
